import { useTranslation } from 'react-i18next';
import {
  ListChecks,
  CheckCircle2,
  Activity,
  Circle,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/core/lib/utils';
import type { OrgSummaryDto, TaskStatusOverviewDto } from '../../types';

interface KpiCardWrapperProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  testId: string;
  highlight?: boolean;
  danger?: boolean;
}

function KpiCardWrapper({
  title,
  value,
  subtitle,
  icon: Icon,
  testId,
  highlight,
  danger,
}: KpiCardWrapperProps) {
  return (
    <Card data-testid={testId} className={cn(highlight && 'border-primary/30 bg-primary/5')}>
      <CardContent className="px-4 py-3">
        <div className="mb-1 flex items-start justify-between gap-1">
          <p className="text-xs font-medium text-muted-foreground leading-tight">{title}</p>
          <Icon
            className={cn(
              'h-3.5 w-3.5 shrink-0 mt-0.5',
              highlight ? 'text-primary' : danger ? 'text-destructive' : 'text-muted-foreground',
            )}
          />
        </div>
        <p
          className={cn(
            'text-3xl font-bold tracking-tight',
            highlight ? 'text-primary' : danger ? 'text-destructive' : '',
          )}
        >
          {value}
        </p>
        <p className="mt-1 text-xs text-muted-foreground truncate">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

export function KpiCardsSection({
  orgSummary,
  taskStatus,
  isLoading,
}: {
  orgSummary: OrgSummaryDto | undefined;
  taskStatus: TaskStatusOverviewDto | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation('analytics');

  if (isLoading) {
    return (
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6"
        data-testid="analytics-kpi-cards-section"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6"
      data-testid="analytics-kpi-cards-section"
    >
      <KpiCardWrapper
        title={t('kpi.totalTasks')}
        value={String(orgSummary?.totalTasks ?? 0)}
        subtitle={t('kpi.subtitles.projects', { count: orgSummary?.totalProjects ?? 0 })}
        icon={ListChecks}
        testId="analytics-kpi-total-tasks"
      />

      <KpiCardWrapper
        title={t('kpi.completedTasks')}
        value={String(orgSummary?.completedTasks ?? 0)}
        subtitle={t('kpi.subtitles.ofTotal', {
          value: (orgSummary?.completedTasksPercentage ?? 0).toFixed(1),
        })}
        icon={CheckCircle2}
        testId="analytics-kpi-completed-tasks"
      />

      <KpiCardWrapper
        title={t('kpi.inProgressTasks')}
        value={String(orgSummary?.inProgressTasks ?? 0)}
        subtitle={t('kpi.subtitles.activeNow')}
        icon={Activity}
        testId="analytics-kpi-in-progress-tasks"
      />

      <KpiCardWrapper
        title={t('kpi.todoTasks')}
        value={String(taskStatus?.todoTasks ?? 0)}
        subtitle={t('kpi.subtitles.pending')}
        icon={Circle}
        testId="analytics-kpi-todo-tasks"
      />

      <KpiCardWrapper
        title={t('kpi.overdueTasks')}
        value={String(orgSummary?.overdueTasks ?? 0)}
        subtitle={t('kpi.subtitles.delayRate', {
          value: (orgSummary?.overdueTasksPercentage ?? 0).toFixed(1),
        })}
        icon={AlertTriangle}
        danger
        testId="analytics-kpi-overdue-tasks"
      />

      <KpiCardWrapper
        title={t('kpi.avgEfficiency')}
        value={`${(orgSummary?.averageEfficiencyPercentage ?? 0).toFixed(1)}%`}
        subtitle={t('kpi.subtitles.estimatedTotal', {
          value: (taskStatus?.totalEstimatedHours ?? 0).toFixed(1),
        })}
        icon={TrendingUp}
        highlight
        testId="analytics-kpi-avg-efficiency"
      />
    </div>
  );
}
